import type { SiteNavigationElement } from 'schema-dts';

import type {
    NavigationItemInput,
    SiteNavigationInput,
    SiteNavigationResult,
    NavigationValidationError,
    SiteNavigationBuilder as SiteNavigationBuilderInterface,
    GeneratorOptions,
    SchemaGenerationResult,
    CompanySchemaValidationError,
} from './types/company';
import { Result, ok, fail, isOk, safeString, stripHtmlTags, isValidUrl, toAbsoluteUrl } from './utils';
import { logger } from './utils/logger';

const siteNavigationCache = new Map<string, SiteNavigationResult>();

function validateNavigationItemInput(input: unknown): Result<NavigationItemInput, NavigationValidationError[]> {
    const errors: NavigationValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'input', message: 'Navigation item input must be an object' }]);
    }

    const item = input as Record<string, unknown>;

    if (!item.name || typeof item.name !== 'string') {
        errors.push({ field: 'name', message: 'Name is required and must be a string', value: item.name });
    }

    if (!item.url || typeof item.url !== 'string') {
        errors.push({ field: 'url', message: 'URL is required and must be a string', value: item.url });
    } else {
        const absoluteUrl = toAbsoluteUrl(item.url);
        if (!isValidUrl(absoluteUrl)) {
            errors.push({ field: 'url', message: 'URL must be a valid URL format', value: item.url });
        }
    }

    if (item.position !== undefined) {
        if (typeof item.position !== 'number' || item.position < 1) {
            errors.push({ field: 'position', message: 'Position must be a positive number', value: item.position });
        }
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok(item as unknown as NavigationItemInput);
}

function validateSiteNavigationInput(input: unknown): Result<SiteNavigationInput, NavigationValidationError[]> {
    const errors: NavigationValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'input', message: 'Site navigation input must be an object' }]);
    }

    const nav = input as Record<string, unknown>;

    if (!nav.items || !Array.isArray(nav.items)) {
        return fail([{ field: 'items', message: 'Items must be an array' }]);
    }

    if (nav.items.length === 0) {
        return fail([{ field: 'items', message: 'At least one navigation item is required' }]);
    }

    nav.items.forEach((item, index) => {
        const validation = validateNavigationItemInput(item);
        if (!isOk(validation)) {
            validation.error.forEach((error) => {
                errors.push({
                    field: `items[${index}].${error.field}`,
                    message: error.message,
                    value: error.value,
                });
            });
        }
    });

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok(nav as unknown as SiteNavigationInput);
}

class SiteNavigationBuilder implements SiteNavigationBuilderInterface {
    private schema: Partial<SiteNavigationResult>;
    private items: SiteNavigationElement[];

    constructor() {
        this.schema = {
            '@type': 'SiteNavigationElement',
        };
        this.items = [];
    }

    addNavigationItem(name: string, url: string, position?: number): SiteNavigationBuilder {
        const absoluteUrl = toAbsoluteUrl(url);
        const navItem: SiteNavigationElement = {
            '@type': 'SiteNavigationElement',
            name: stripHtmlTags(safeString(name)),
            url: absoluteUrl,
        };

        if (position !== undefined && position > 0) {
            navItem.position = position;
        }

        this.items.push(navItem);
        return this;
    }

    setName(name: string): SiteNavigationBuilder {
        this.schema.name = stripHtmlTags(safeString(name));
        return this;
    }

    setDescription(description: string): SiteNavigationBuilder {
        this.schema.description = stripHtmlTags(safeString(description));
        return this;
    }

    build(): SiteNavigationResult {
        const sortedItems = this.items.sort((a, b) => {
            const posA = Number(a.position) || 999;
            const posB = Number(b.position) || 999;
            return posA - posB;
        });

        if (!this.schema.name) {
            this.schema.name = 'Site Navigation';
        }

        return {
            ...this.schema,
            hasPart: sortedItems,
        } as SiteNavigationResult;
    }
}

export function generateSiteNavigationSchema(
    input: SiteNavigationInput,
    options: GeneratorOptions = {},
): SchemaGenerationResult<SiteNavigationResult> {
    const cacheKey = `navigation-${JSON.stringify(input)}-${JSON.stringify(options)}`;

    if (!options.skipCache && siteNavigationCache.has(cacheKey)) {
        const cached = siteNavigationCache.get(cacheKey)!;
        logger.debug('Retrieved site navigation schema from cache', { cacheKey });
        return {
            schema: cached,
            metadata: {
                generatedAt: new Date().toISOString(),
                cacheHit: true,
                validationPassed: true,
            },
        };
    }

    const validation = validateSiteNavigationInput(input);
    if (!isOk(validation)) {
        logger.warn('Invalid site navigation input', { errors: validation.error });
        return {
            schema: {} as SiteNavigationResult,
            metadata: {
                generatedAt: new Date().toISOString(),
                cacheHit: false,
                validationPassed: false,
                errors: validation.error as CompanySchemaValidationError[],
            },
        };
    }

    const validInput = validation.data;

    const builder = new SiteNavigationBuilder();

    if (validInput.name) {
        builder.setName(validInput.name);
    }

    if (validInput.description) {
        builder.setDescription(validInput.description);
    }

    validInput.items.forEach((item) => {
        builder.addNavigationItem(item.name, item.url, item.position);
    });

    const schema = builder.build();

    if (!options.skipCache) {
        siteNavigationCache.set(cacheKey, schema);
    }

    logger.info('Generated site navigation schema', {
        name: schema.name,
        itemCount: schema.hasPart?.length || 0,
    });

    return {
        schema,
        metadata: {
            generatedAt: new Date().toISOString(),
            cacheHit: false,
            validationPassed: true,
        },
    };
}
